/**
 * Boundary normalizer for BYOD widget responses (`/query` and `/preview`).
 *
 * Runs at every fetch boundary so no downstream component sees a raw wire
 * shape. It does two things:
 *
 * 1. Resolves Python `decimal.Decimal` serialization artifacts
 *    (see `normalizeDecimalValues`).
 * 2. Flattens KPI payloads. The backend nests KPI metrics under a `data` key:
 *      { widget_type: 'kpi', data: { value } }
 *    but components consume the flat `PreviewResponseKpi`. We spread that nested
 *    `data` up to the top level so `value` (incl. a real `0`) reaches the tile
 *    instead of reading `undefined` → "N/A".
 *
 * The KPI flatten only fires for `kpi` with an object-shaped `data`, so it's a
 * no-op for already-flat responses (a flat KPI has no `data` key) and never
 * disturbs `chart` (whose `data` is an array of rows).
 *
 * 3. Flattens table `entities_attributes` (see `flattenTableEntityAttributes`):
 *    hoists each row's nested entity-attribute object up to top-level row keys
 *    and surfaces the key union as `all_attributes_columns`. No-op for tables
 *    without entity attributes.
 */

import {WIDGET_TYPE} from 'src/types/byod';

import {normalizeDecimalValues} from './normalize-decimal';
import {flattenTableEntityAttributes} from './flatten-entity-attributes';

// Widget types whose metrics the backend nests under `data`. Chart/table are
// excluded: chart's `data` is its row array (must stay nested), table is flat.
const FLATTENED_WIDGET_TYPES: ReadonlySet<string> = new Set([WIDGET_TYPE.KPI]);

export function normalizePreviewResponse<T>(value: T): T {
  const normalized = normalizeDecimalValues(value);
  if (
    normalized == null ||
    typeof normalized !== 'object' ||
    Array.isArray(normalized)
  ) {
    return normalized;
  }
  const obj = normalized as Record<string, unknown>;
  if (
    FLATTENED_WIDGET_TYPES.has(obj.widget_type as string) &&
    obj.data != null &&
    typeof obj.data === 'object' &&
    !Array.isArray(obj.data)
  ) {
    const {data: payload, ...rest} = obj;
    return {...rest, ...(payload as Record<string, unknown>)} as T;
  }
  if (obj.widget_type === WIDGET_TYPE.TABLE) {
    return flattenTableEntityAttributes(normalized);
  }
  return normalized;
}
